import { useLocation, useNavigate } from "react-router-dom"
import { useMobileViewport } from "../../hooks/useMobileViewport"
import { BearingComposerSheet } from "./BearingComposerSheet"

/**
 * Mounts the bearing composer sheet on every authenticated in-app route EXCEPT
 * the Orca shell itself, which mounts its own instance inside the chat pane.
 *
 * Two things make an off-shell mount work:
 *  1. `data-bearing-scope` — the --brg-* tokens are otherwise scoped to the shell
 *     root, so outside it the sheet would render unstyled.
 *  2. A handoff — off-shell there is no ChatPane listening for `orca:submitChat`,
 *     so a send would go nowhere. We stash the message and navigate to the shell,
 *     which drains it on mount.
 */

/** Routes where OrcaLayout already mounts a sheet — never double-mount. */
const SHELL_ROUTES = ["/dash", "/hub", "/lens", "/lab"]

/** sessionStorage key shared with ChatPane's drain-on-mount. */
export const PENDING_COMPOSER_MESSAGE_KEY = "orca:pendingComposerMessage"

interface GlobalComposerSheetProps {
  /** Only rendered for a signed-in user on an in-app page. */
  enabled: boolean
}

export function GlobalComposerSheet({ enabled }: GlobalComposerSheetProps) {
  const { isMobile } = useMobileViewport()
  const location = useLocation()
  const navigate = useNavigate()

  const onShellRoute = SHELL_ROUTES.some(
    (route) => location.pathname === route || location.pathname.startsWith(`${route}/`),
  )

  if (!enabled || !isMobile || onShellRoute) return null

  const goToShellWith = (message?: string) => {
    if (message) {
      try {
        sessionStorage.setItem(PENDING_COMPOSER_MESSAGE_KEY, message)
      } catch {
        // Private mode / quota — fall through and still navigate.
      }
    }
    navigate("/dash")
  }

  return (
    <div className="bearing-global-composer-layer" data-bearing-scope="light">
      <BearingComposerSheet
        onSend={(message) => goToShellWith(message)}
        // Attachments need the shell's upload + conversation context, so the
        // sheet hands off rather than staging a file with nowhere to send it.
        onAttachFiles={() => goToShellWith()}
        onTakePhoto={() => goToShellWith()}
      />
    </div>
  )
}

export default GlobalComposerSheet
